import type { AppContext } from '../app';
import {
  MASCOT_CATEGORIES,
  classify,
  type ClassifierMatch,
  type ClassifierResult,
  type MascotCategory,
} from './MascotClassifier';

/**
 * Phase 71 partial — personal moderation mascot manager.
 *
 * Owns the per-user mascot state machine + per-category action settings
 * + kid-mode lock + "what got filtered today" rolling log. L2 / UDC
 * integration is deferred to Phase 71 full (requires Phase 67 UDC
 * predicate engine).
 *
 * Settings + log persist to localStorage so they survive page reload
 * without needing a Dexie migration. The mascot is intentionally
 * client-local — peers should not know which categories another user
 * filters.
 */

export type MascotAction = 'hide' | 'blur' | 'pass';

export type MascotState =
  /** No recent activity; sigil sits at rest. */
  | { kind: 'idle' }
  /** Mascot has evaluated content in the recent window; sigil shows watching. */
  | { kind: 'watching'; sinceMs: number }
  /** A flag fired in the last few seconds; sigil glows. */
  | { kind: 'just-flagged'; firedAt: number; category: MascotCategory }
  /** User opened the drawer; sigil dimmed while UI is in foreground. */
  | { kind: 'summoned' };

export interface MascotSettings {
  perCategory: Record<MascotCategory, MascotAction>;
  kidMode: { enabled: boolean; pinHash: string | null };
}

export interface FilterLogEntry {
  ts: string;
  contentHash: string;
  category: MascotCategory;
  matchedTerms: string[];
  action: MascotAction;
}

export interface EvaluateOptions {
  /** Stable identifier (often a content hash) to keep the log distinct from raw content. */
  contentHash: string;
}

export interface EvaluateOutcome {
  /** Strongest action across all flagged categories ('hide' > 'blur' > 'pass'). */
  action: MascotAction;
  result: ClassifierResult;
}

const SETTINGS_KEY = 'alds-mascot-settings-v1';
const LOG_KEY = 'alds-mascot-log-v1';
const LOG_MAX = 200;
const JUST_FLAGGED_HOLD_MS = 5_000;

const DEFAULT_SETTINGS: MascotSettings = {
  perCategory: {
    violence:        'blur',
    sexual:          'hide',
    doxing:          'hide',
    hate:            'hide',
    'kid-safety':    'hide',
    'location-leak': 'blur',
    'scam-pattern':  'blur',
  },
  kidMode: { enabled: false, pinHash: null },
};

const KID_MODE_OVERRIDE: Record<MascotCategory, MascotAction> = {
  violence:        'hide',
  sexual:          'hide',
  doxing:          'hide',
  hate:            'hide',
  'kid-safety':    'hide',
  'location-leak': 'hide',
  'scam-pattern':  'hide',
};

export class MascotManager {
  readonly #ctx: AppContext;
  #settings: MascotSettings = cloneSettings(DEFAULT_SETTINGS);
  #log: FilterLogEntry[] = [];
  #state: MascotState = { kind: 'idle' };
  readonly #stateSubs = new Set<(s: MascotState) => void>();
  readonly #settingsSubs = new Set<(s: MascotSettings) => void>();
  readonly #logSubs = new Set<(l: readonly FilterLogEntry[]) => void>();

  constructor(ctx: AppContext) {
    this.#ctx = ctx;
    this.#loadFromStorage();
  }

  /** Read-only snapshot of mascot state. */
  state(): MascotState { return this.#state; }

  /** Read-only snapshot of settings. */
  settings(): MascotSettings { return cloneSettings(this.#settings); }

  /** Read-only snapshot of filter log (newest last). */
  log(): readonly FilterLogEntry[] { return this.#log; }

  /** Subscribe to state changes; fires immediately with current state. */
  onState(fn: (s: MascotState) => void): () => void {
    this.#stateSubs.add(fn);
    fn(this.#state);
    return () => this.#stateSubs.delete(fn);
  }

  /** Subscribe to settings changes; fires immediately with current settings. */
  onSettings(fn: (s: MascotSettings) => void): () => void {
    this.#settingsSubs.add(fn);
    fn(this.settings());
    return () => this.#settingsSubs.delete(fn);
  }

  /** Subscribe to log changes; fires immediately with current log. */
  onLog(fn: (l: readonly FilterLogEntry[]) => void): () => void {
    this.#logSubs.add(fn);
    fn(this.#log);
    return () => this.#logSubs.delete(fn);
  }

  /**
   * Evaluate a piece of text, append matching categories to the filter
   * log, and return the strongest action across categories. Callers
   * decide whether to render, blur, or hide the content.
   */
  evaluate(text: string, opts: EvaluateOptions): EvaluateOutcome {
    const result = classify(text);
    if (result.matches.length === 0) {
      // Clean content still moves us into 'watching' (mascot is awake).
      this.#enterWatching();
      return { action: 'pass', result };
    }
    const action = this.#strongestAction(result.matches);
    for (const m of result.matches) {
      this.#appendLog({
        ts: new Date().toISOString(),
        contentHash: opts.contentHash,
        category: m.category,
        matchedTerms: [...m.matchedTerms],
        action: this.#actionFor(m.category),
      });
    }
    const firedCategory = result.matches[0]?.category;
    if (firedCategory !== undefined) {
      this.#enterJustFlagged(firedCategory);
    }
    return { action, result };
  }

  /** Open the drawer — moves state to 'summoned'. */
  summon(): void {
    this.#updateState({ kind: 'summoned' });
  }

  /**
   * Close the drawer — returns to watching/idle depending on recent activity.
   * Note: this writes state directly rather than calling #enterWatching,
   * because #enterWatching short-circuits when the current state is
   * 'summoned' (to prevent background evaluate() calls from bumping the
   * user out of an open drawer). Dismiss is the intentional exit.
   */
  dismiss(): void {
    const hasRecentActivity = this.#log.some((e) => Date.now() - new Date(e.ts).getTime() < JUST_FLAGGED_HOLD_MS);
    if (hasRecentActivity) {
      this.#updateState({ kind: 'watching', sinceMs: Date.now() });
    } else {
      this.#updateState({ kind: 'idle' });
    }
  }

  /** Update the action for a single category (e.g. user wants to UN-hide kid-safety). */
  setCategoryAction(category: MascotCategory, action: MascotAction): void {
    if (this.#settings.kidMode.enabled) {
      throw new Error('cannot change category action while kid-mode is locked; unlock with PIN first');
    }
    const next = cloneSettings(this.#settings);
    next.perCategory[category] = action;
    this.#applySettings(next);
  }

  /**
   * Enable kid-mode. Snapshots current settings (so unlock can restore
   * them), overrides every category to 'hide', and stores a hashed PIN
   * for the unlock gate. PIN is hashed locally — never leaves device.
   */
  async enableKidMode(pin: string): Promise<void> {
    if (pin.length < 4) throw new Error('PIN must be at least 4 characters');
    const pinHash = await hashPin(pin);
    const next = cloneSettings(this.#settings);
    next.kidMode = { enabled: true, pinHash };
    // Don't clobber user's pre-kid-mode per-category settings — they're
    // restored on unlock. KID_MODE_OVERRIDE applies at action-lookup time.
    this.#applySettings(next);
  }

  /** Disable kid-mode by verifying the PIN. Restores prior per-category settings. */
  async disableKidMode(pin: string): Promise<boolean> {
    if (!this.#settings.kidMode.enabled || this.#settings.kidMode.pinHash === null) return false;
    const candidate = await hashPin(pin);
    if (candidate !== this.#settings.kidMode.pinHash) return false;
    const next = cloneSettings(this.#settings);
    next.kidMode = { enabled: false, pinHash: null };
    this.#applySettings(next);
    return true;
  }

  /** Clear the filter log (one-tap "I've reviewed it" affordance). */
  clearLog(): void {
    this.#log = [];
    try { localStorage.removeItem(LOG_KEY); } catch { /* noop */ }
    this.#emitLog();
  }

  /** Return the effective action for a category, honoring kid-mode lock. */
  #actionFor(category: MascotCategory): MascotAction {
    if (this.#settings.kidMode.enabled) return KID_MODE_OVERRIDE[category];
    return this.#settings.perCategory[category];
  }

  #strongestAction(matches: readonly ClassifierMatch[]): MascotAction {
    let strongest: MascotAction = 'pass';
    for (const m of matches) {
      const a = this.#actionFor(m.category);
      if (a === 'hide') return 'hide';
      if (a === 'blur' && strongest === 'pass') strongest = 'blur';
    }
    return strongest;
  }

  #enterWatching(): void {
    if (this.#state.kind === 'summoned') return;
    this.#updateState({ kind: 'watching', sinceMs: Date.now() });
  }

  #enterJustFlagged(category: MascotCategory): void {
    if (this.#state.kind === 'summoned') return;
    this.#updateState({ kind: 'just-flagged', firedAt: Date.now(), category });
  }

  #updateState(next: MascotState): void {
    this.#state = next;
    for (const fn of this.#stateSubs) fn(next);
  }

  #applySettings(next: MascotSettings): void {
    this.#settings = next;
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* noop */ }
    for (const fn of this.#settingsSubs) fn(this.settings());
  }

  #appendLog(entry: FilterLogEntry): void {
    this.#log = [...this.#log, entry].slice(-LOG_MAX);
    try { localStorage.setItem(LOG_KEY, JSON.stringify(this.#log)); } catch { /* noop */ }
    this.#emitLog();
  }

  #emitLog(): void {
    for (const fn of this.#logSubs) fn(this.#log);
  }

  #loadFromStorage(): void {
    try {
      const rawSettings = localStorage.getItem(SETTINGS_KEY);
      if (rawSettings !== null) {
        const parsed = JSON.parse(rawSettings) as Partial<MascotSettings>;
        const merged = cloneSettings(DEFAULT_SETTINGS);
        if (parsed.perCategory !== undefined) {
          for (const cat of MASCOT_CATEGORIES) {
            const action = parsed.perCategory[cat];
            if (action === 'hide' || action === 'blur' || action === 'pass') {
              merged.perCategory[cat] = action;
            }
          }
        }
        if (parsed.kidMode !== undefined && typeof parsed.kidMode === 'object' && parsed.kidMode !== null) {
          merged.kidMode = {
            enabled: parsed.kidMode.enabled === true,
            pinHash: typeof parsed.kidMode.pinHash === 'string' ? parsed.kidMode.pinHash : null,
          };
        }
        this.#settings = merged;
      }
    } catch { /* corrupt storage — fall back to defaults */ }
    try {
      const rawLog = localStorage.getItem(LOG_KEY);
      if (rawLog !== null) {
        const parsed = JSON.parse(rawLog) as FilterLogEntry[];
        if (Array.isArray(parsed)) this.#log = parsed.slice(-LOG_MAX);
      }
    } catch { /* swallow */ }
  }
}

function cloneSettings(s: MascotSettings): MascotSettings {
  return {
    perCategory: { ...s.perCategory },
    kidMode: { ...s.kidMode },
  };
}

async function hashPin(pin: string): Promise<string> {
  if (typeof crypto === 'undefined' || crypto.subtle === undefined) {
    // Test fallback — non-cryptographic but stable for unit tests.
    let h = 0;
    for (let i = 0; i < pin.length; i++) h = (h * 31 + pin.charCodeAt(i)) | 0;
    return `fallback:${h.toString(16)}`;
  }
  const enc = new TextEncoder().encode(`alds-mascot-pin:${pin}`);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const MASCOT_MANAGER_KEY = Symbol.for('alds.mascotManager');

/** Per-AppContext singleton accessor. Mirrors getPersonalHelper / getCallManager. */
export function getMascotManager(ctx: AppContext): MascotManager {
  const bag = ctx as unknown as Record<symbol, MascotManager | undefined>;
  let manager = bag[MASCOT_MANAGER_KEY];
  if (manager === undefined) {
    manager = new MascotManager(ctx);
    bag[MASCOT_MANAGER_KEY] = manager;
  }
  return manager;
}
